import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import { API, toLocalInput } from "./utils";

/* ---------- Editor Modal ---------- */
export default function WebinarEditor({ webinar, onClose, onSaved, token }) {
  const isNew = !webinar;
  const [title, setTitle] = useState(webinar?.title || "");
  const [link, setLink] = useState(webinar?.zoho_link || "");
  const [adminLink, setAdminLink] = useState(webinar?.zoho_admin_link || "");
  const [scheduledAt, setScheduledAt] = useState(
    webinar?.scheduled_at ? toLocalInput(webinar.scheduled_at) : ""
  );
  const [description, setDescription] = useState(webinar?.description || "");
  const [pitchOneLiner, setPitchOneLiner] = useState(webinar?.pitch_one_liner || "");
  const [bullets, setBullets] = useState(
    (webinar?.bullets && webinar.bullets.length ? webinar.bullets : ["", "", "", ""]).slice(0, 6),
  );
  const [kind, setKind] = useState(webinar?.kind || "regular");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim() || !link.trim() || !scheduledAt) {
      toast.error("Bitte alle Pflichtfelder ausfüllen."); return;
    }
    setBusy(true);
    try {
      const iso = new Date(scheduledAt).toISOString();
      const payload = {
        title, zoho_link: link, zoho_admin_link: adminLink || null,
        scheduled_at: iso, description,
        pitch_one_liner: pitchOneLiner.trim() || null,
        bullets: bullets.map((b) => b.trim()).filter(Boolean),
        kind,
      };
      if (isNew) {
        await axios.post(`${API}/admin/webinars`, payload, { headers: { "X-Admin-Token": token } });
        toast.success("Webinar angelegt.");
      } else {
        await axios.patch(`${API}/admin/webinars/${webinar.id}`, payload, { headers: { "X-Admin-Token": token } });
        toast.success("Webinar aktualisiert.");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="webinar-editor">
      <form onSubmit={save} className="bg-white rounded-2xl w-full max-w-xl p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-lg">{isNew ? "Neues Webinar anlegen" : `Webinar bearbeiten`}</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <Field label="Titel *">
            <input
              type="text" required value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="webinar-title" placeholder="Alphalane Live-Webinar Mai" className="al-input"
            />
          </Field>
          <Field label="Zoho-Anmeldelink *">
            <input
              type="url" required value={link} onChange={(e) => setLink(e.target.value)}
              data-testid="webinar-link" placeholder="https://live.zoho.eu/xaru-nzn-dbz oder https://webinar.zoho.eu/meeting/register?..." className="al-input font-mono text-sm"
            />
          </Field>
          <Field label="Zoho Admin-Link (für Registranten-Sync)">
            <input
              type="url" value={adminLink} onChange={(e) => setAdminLink(e.target.value)}
              data-testid="webinar-admin-link"
              placeholder="https://webinar.zoho.eu/webinar/20114644122/805574000000008009/webinar/details/805574000000008123/1274352136?tab=info"
              className="al-input font-mono text-sm"
            />
            <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
              Optional. Öffne dein Webinar in Zoho und kopiere die URL aus der Adresszeile (Format <code>.../webinar/details/&lt;webinarKey&gt;/&lt;sessionId&gt;</code>) — daraus zieht das System Organisations-ID, Webinar-Key und Session-ID für Registranten- und Teilnehmer-Sync.
            </p>
          </Field>
          <Field label="Datum & Uhrzeit (Europe/Zurich) *">
            <input
              type="datetime-local" required value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}
              data-testid="webinar-datetime" className="al-input"
            />
          </Field>
          <Field label="Kurz-Beschreibung (optional)">
            <textarea
              rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
              data-testid="webinar-description" maxLength={600}
              placeholder="Live-Demo, Track-Record, offene Q&A — bring deine Fragen mit."
              className="al-input resize-none"
            />
          </Field>
          <Field label="Webinar-Typ">
            <div className="flex gap-2" data-testid="webinar-kind">
              {[
                { v: "regular", label: "Regulär", hint: "Verkaufs-/Info-Webinar — wird automatisch in Post-Mails beworben" },
                { v: "technical", label: "Technik", hint: "Setup-/Anleitungs-Webinar — wird NIE automatisch beworben, du lädst manuell ein" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setKind(opt.v)}
                  data-testid={`webinar-kind-${opt.v}`}
                  className={`flex-1 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${
                    kind === opt.v
                      ? "border-[var(--al-purple)] bg-violet-50 text-[var(--al-purple)]"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                  title={opt.hint}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              {kind === "technical"
                ? "⚠️ Dieses Webinar wird NICHT in Post-Webinar-Mails als nächster Termin verlinkt. Lade die Teilnehmer manuell ein (z.B. via Broadcast)."
                : 'Wird automatisch in Post-Webinar-Mails als „nächster Termin" beworben und erscheint im /ai-Hero.'}
            </p>
          </Field>
          <div className="border-t border-slate-200 pt-3">
            <p className="text-[11px] uppercase tracking-widest font-bold text-violet-700 mb-2">
              Hero-Anzeige auf /ai (Lead-Magnet-Seite)
            </p>
          </div>
          <Field label="1-Satz-Pitch (erscheint unter dem Titel auf /ai)">
            <input
              type="text" value={pitchOneLiner} onChange={(e) => setPitchOneLiner(e.target.value)}
              data-testid="webinar-pitch" maxLength={200}
              placeholder="z. B. Live mit Pascal & Adrian — sieh die KI in Aktion und bring deine Fragen mit."
              className="al-input"
            />
          </Field>
          <Field label='Highlights / Bullets („Was du erfährst")'>
            <div className="space-y-2">
              {bullets.map((b, i) => (
                <input
                  key={i}
                  type="text"
                  value={b}
                  onChange={(e) => {
                    const next = [...bullets];
                    next[i] = e.target.value;
                    setBullets(next);
                  }}
                  maxLength={80}
                  placeholder={`Highlight ${i + 1}${i === 0 ? " (z. B. Live-Demo der KI)" : ""}`}
                  data-testid={`webinar-bullet-${i}`}
                  className="al-input text-sm"
                />
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              Leere Felder werden ignoriert. Max 6, optimal 3–4 prägnante Punkte.
            </p>
          </Field>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="al-cta-ghost">Abbrechen</button>
          <button type="submit" disabled={busy} data-testid="webinar-save" className="al-cta disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {isNew ? "Anlegen" : "Speichern"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5 inline-block">{label}</span>
      {children}
    </label>
  );
}
